import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { lottieAnimations, TimedProgressPage } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { BoosterLoadError } from './components/BoosterLoadError';
import { useAgentContext } from './GeneratingPlanPageContainer.agentContext';
import { useBoosterPlan } from './hooks/useBoosterPlan';

const STRINGS = appStrings.modules.satActBooster.generatingPlan;
const GENERATING_DURATION_MS = 4000;

/**
 * The seam between the wizard and the plan: an animation on a timer, then the plan. The run is
 * already finalized before this page mounts, so it does no work and is safe to re-enter — which is
 * the point. It gives the plan a history entry of its own, so backing out of the plan (or out of a
 * tab the plan opened) returns to the plan instead of an emptied wizard.
 *
 * Renders without the page header image, unlike the pages either side of it: the animation is a
 * full-height takeover, and stacking the image above it pushes the card past the fold.
 *
 * The animation doubles as the loading state for the plan read, so entering cold (a reload or a
 * back-nav) shows it rather than a spinner ahead of it.
 */
export const GeneratingPlanPageContainer = () => {
  const navigate = useNavigate();
  const { isPending, loadFailed, hasPlan, refetch } = useBoosterPlan();
  const [timerDone, setTimerDone] = useState(false);

  useAgentContext();

  // Leaving is gated on the read as well as the timer, so a plan that lands after the bar fills
  // still advances instead of stranding the student on a finished animation.
  useEffect(() => {
    if (timerDone && hasPlan) {
      navigate(APP_ROUTE.SAT_ACT_BOOSTER_RESULT);
    }
  }, [timerDone, hasPlan, navigate]);

  if (loadFailed) {
    return <BoosterLoadError onRetry={refetch} />;
  }

  // Nothing finished to animate towards → the wizard, to build one. Only once the read has settled:
  // the finalize that precedes this page invalidates the submission, so its refetch is in flight on
  // mount, and deciding then would bounce a student who does have a plan.
  if (!isPending && !hasPlan) {
    return <Navigate to={APP_ROUTE.SAT_ACT_BOOSTER} replace />;
  }

  return (
    <TimedProgressPage
      title={STRINGS.title}
      animationData={lottieAnimations.buildingIdeas}
      durationMs={GENERATING_DURATION_MS}
      onComplete={() => setTimerDone(true)}
      showCard={false}
      titlePlacement="bottom"
      titleVariant="display-xs-semibold"
    />
  );
};
